import { GridColDef } from "@mui/x-data-grid-pro";
import {
  DeleteIcon,
  GKButton,
  GKClientDataGrid,
  Grid2,
  HelpIcon,
  IconButton,
  NoData,
  PlusIcon,
  PopConfirm,
  Show,
  StatusField,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField, matchDate, refetchQuery } from "utils/helpers";
import TelegramGuideModal from "../EquityBasketViewDetails/TelegramGuideModal";
import TelegramModal from "./TelegramModal";

const TelegramTab = () => {
  const [openModal, setOpenModal] = useState(false);

  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);

  const [editData, setEditData] = useState<any>(null);

  const [confirmationModal, setConfirmationModal] =
    useState<HTMLElement | null>(null);
  const [selectedTelegramId, setSelectedTelegramId] = useState<string | null>(
    null,
  );

  const { id } = useParams<{ id: string }>();

  const { data } = useFetchQuery({
    key: ["TELEGRAM_CHANNELS", id],
    route: `/telegram-channels/?basket=${id}`,
  });

  const { mutate: deleteTelegram } = useMutation({
    mutationFn: (telegramId: string) =>
      axiosInstance.delete(`/telegram-channels/${telegramId}`),
    onSuccess: (response) => {
      toast.success(response.data?.message || "Telegram channel deleted!");
      refetchQuery("TELEGRAM_CHANNELS");
      setConfirmationModal(null);
      setSelectedTelegramId(null);
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to delete Telegram channel",
      );
      setConfirmationModal(null);
      setSelectedTelegramId(null);
    },
  });

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Channel Name", "channelName", 120, 1),
    },
    {
      ...extraColumnsField("Channel Id", "channelId", 120, 1),
    },
    {
      ...extraColumnsField("Status", "isActive", 120, 1),
      renderCell: (params) => (
        <StatusField
          statusString={params.row.isActive ? "approved" : "pending"}
        />
      ),
    },
    {
      ...extraColumnsField("Created", "createdAt", 120, 1),
      renderCell: (value) => (
        <Typography>{matchDate(value.row.createdAt) || "N/A"}</Typography>
      ),
    },
    {
      ...extraColumnsField("Action", "action", 0, 1),
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <IconButton
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            setConfirmationModal(e.currentTarget);
            setSelectedTelegramId(params.row.id);
          }}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Show.When isTrue={data?.data?.data?.length === 0}>
        <Grid2 size={12}>
          <NoData
            customImg={
              <img
                loading="lazy"
                alt=""
                src="/LandingPage/telegram.svg"
                draggable={"false"}
              />
            }
            text="No Telegram channel or group connected"
            subText="This basket has no linked Telegram channel or group yet."
            additionalComponent={
              <Grid2 container spacing={1}>
                <Grid2 size={12}>
                  <GKButton
                    variant="outlined"
                    startIcon={<HelpIcon />}
                    onClick={() => setIsGuideModalOpen(true)}
                    sx={{
                      mr: 1,
                      color: "var(--grey-900)",
                      borderColor: "var(--neutral-100)",
                      "&:hover": {
                        color: "var(--grey-900)",
                      },
                    }}
                  >
                    How to Guide
                  </GKButton>
                  <GKButton
                    variant="contained"
                    startIcon={<PlusIcon />}
                    onClick={() => setOpenModal(true)}
                  >
                    Add New
                  </GKButton>
                </Grid2>
              </Grid2>
            }
          />
        </Grid2>
      </Show.When>

      <Show.When isTrue={data?.data?.data?.length > 0}>
        <Grid2 size={8}>
          <GKClientDataGrid
            // sx={{
            //   maxWidth: "867px",
            // }}
            columns={columns}
            rows={data?.data?.data || []}
            name="telegramTab"
            onRowClick={(params) => {
              setEditData(params.row);
              setOpenModal(true);
            }}
            headerComponent={
              <Grid2 container direction="row" spacing={1}>
                <GKButton
                  variant="outlined"
                  startIcon={<HelpIcon />}
                  onClick={() => setIsGuideModalOpen(true)}
                  sx={{
                    mr: 1,
                    color: "var(--grey-900)",
                    borderColor: "var(--neutral-100)",
                    "&:hover": {
                      color: "var(--grey-900)",
                    },
                  }}
                >
                  How to Guide
                </GKButton>
                <GKButton
                  variant="contained"
                  startIcon={<PlusIcon />}
                  onClick={() => {
                    setEditData(null);
                    setOpenModal(true);
                  }}
                >
                  Add New
                </GKButton>
              </Grid2>
            }
          />
        </Grid2>
      </Show.When>

      {openModal && (
        <TelegramModal
          open={openModal}
          setOpen={setOpenModal}
          editData={editData}
          telegramId={editData?.id}
        />
      )}

      {isGuideModalOpen && (
        <TelegramGuideModal
          open={isGuideModalOpen}
          setOpen={setIsGuideModalOpen}
        />
      )}

      {confirmationModal && selectedTelegramId && (
        <PopConfirm
          anchorEl={confirmationModal}
          open={Boolean(confirmationModal)}
          setOpen={setConfirmationModal}
          onSuccess={() => deleteTelegram(selectedTelegramId)}
        />
      )}
    </>
  );
};

export default TelegramTab;
